import {
  type CompletePath,
  type ID,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { artBoardActions } from '@store/reducers/artboard'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { delay, put, select } from 'redux-saga/effects'

import { GlobalActions } from '@/store'

// Centres the canvas on a page and selects it — the same pair of effects the
// search results have always produced (see useOnSelect).
//
// The part search never did: a page whose parents are collapsed is not in the
// DOM at all, and `jumpTo` measures a DOM node, so the jump was a silent no-op.
// Search covered that with a "target page is collapsed" notification whose
// callback was left unimplemented. Here the ancestors are expanded first.
//
// Only ancestors that are actually collapsed are touched, so a click on an
// already-visible page does not commit anything. For a viewer without edit
// access the expansion is dispatched locally instead of committed: it is their
// own view that has to change, not the stored project.
export function* jumpToPage({
  payload: { id },
}: ReturnType<typeof artBoardActions.jumpToPage>) {
  const path: CompletePath = yield select(
    migrationProjectSelector.getCompletePath(id as ID)
  )

  if (!path) {
    return
  }

  const [, sectionId, nodeIds] = path

  const ancestorIds = nodeIds.filter((nodeId) => nodeId !== id)

  const collapsedAncestorIds: ID[] = []

  for (const nodeId of ancestorIds) {
    const node: { collapsed: boolean } = yield select(
      migrationProjectSelector.getProjectNodeById(nodeId)
    )

    if (node.collapsed) {
      collapsedAncestorIds.push(nodeId)
    }
  }

  const sectionCollapsed: boolean = sectionId
    ? yield select(
        migrationProjectSelector.getProjectSectionIsCollapsed(sectionId)
      )
    : false

  const expandActions = [
    ...(sectionCollapsed
      ? [
          ProjectActions.sections.changeCollapse({
            ids: [sectionId as ID],
            collapsed: false,
          }),
        ]
      : []),
    ...(collapsedAncestorIds.length
      ? [
          ProjectActions.nodes.changeCollapse({
            ids: collapsedAncestorIds,
            collapsed: false,
          }),
        ]
      : []),
  ]

  if (expandActions.length) {
    const canEdit: boolean = yield select(
      migrationProjectSelector.getProjectAccessCanEdit
    )

    if (canEdit) {
      yield put({
        // Expanding an ancestor to reach a page is navigation, not an edit.
        // Without this flag historyMiddleware pushes an undo snapshot for it,
        // and the next Ctrl+Z rolls back the expansion instead of whatever the
        // user actually changed last.
        ...commitProjectRequest(...expandActions),
        history: false,
      })
    } else {
      for (const action of expandActions) {
        yield put(action)
      }
    }

    // The freshly uncollapsed pages have to be in the DOM before jumpTo reads
    // their position — same reason jumpToThread waits here.
    yield delay(1)
  }

  yield put(
    GlobalActions.jumpTo({
      selector: 'id',
      value: id,
    })
  )

  yield put({
    type: CanvasActionTypes.SELECT_NODES,
    payload: [id],
  })
}
